import { mapAsync, uniq } from 'es-toolkit';

import { assertUnlessInProduction, getMostRecentAnalysis } from '@paragon/client-tools';
import {
  CalculatedLine,
  ComponentDesign,
  ComponentDesignLayoutModel,
  isTruss,
  Point2D,
  TrussEnvelope,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { Point2D as Point2DClass, point2DEquals } from '@paragon/geometry-library';
import { Point2DInterface } from '@paragon/geometry-library/source/scripts/Point2D';
import { getInsertionsWithPendingChanges } from '@paragon/layout-models';
import { hasAssemblyPermission, hasMultiSelectPermission } from '@paragon/login-tools';
import {
  getMostCommonPlateSize,
  getMostCommonPlateType,
  getPlateSizeString,
  getPlateTypeString,
} from '@paragon/material-lists';
import { closeAllSnackbars, enqueueError, enqueueInfo, enqueueWarning } from '@paragon/notification-tools';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { pickSingleActiveBuildingGuid } from '../../library/hierarchy-filtering/active-building';
import { getSelectedItemIdentifiers, getSingleSelectedItemIdentifierOrNull } from '../../library/selection/selectors';
import {
  createSelectedItemEntry,
  isConsideredDesignItem,
  isConsideredLayoutItem,
  SelectableItemEntry,
  SelectableItemOfType,
  SelectableItemType,
} from '../../library/selection/types';
import { focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { matchUrlPattern } from '../../urlPattern';
import { getBasicInsertionInformation, isBasicInsertionModeActive } from '../selectors/basicInsertionSelectors';
import {
  getNextComponentDesign,
  getNextGroupedProductionComponentInstance,
  getPreviousComponentDesign,
  getPreviousGroupedProductionComponentInstance,
} from '../selectors/componentNavigationSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getActiveStation } from '../selectors/getActiveStation';
import { getCurrentlySelectableItemTypes } from '../selectors/getCurrentlyClickableItemTypes';
import { getLoadCasesForActiveComponentDesign } from '../selectors/getLoadCasesForActiveComponentDesignSelector';
import { getProductionComponentInstanceByGuid } from '../selectors/getProductionComponentInstanceByGuid';
import {
  getSelectedEntryFactory,
  getSelectedItemEntries,
  getSingleSelectedItemEntryOrNull,
} from '../selectors/getSelectedItemSelector';
import { isInsertingComponentDesign } from '../selectors/isInsertingComponentDesignSelector';
import { ITEMS_ALWAYS_MULTI_SELECTABLE } from '../selectors/isMultiSelectableItemTypeSelector';
import {
  createGetLevelGuid,
  getActiveLayoutLevelGuidOrNull,
  getAllBuildingGuids,
  getAllLevelGuids,
  getTrussEnvelopeGuidsByComponentDesignGuid,
  getVisibleTrussEnvelopeGuidsByComponentDesignGuid,
  hasLevelGuid,
} from '../selectors/layoutDataSelectors';
import { is3DLayoutCameraInUse, isLayoutModeEnabled, isLayoutViewActive } from '../selectors/layoutModeSelectors';
import { getPickingObjectInfo } from '../selectors/pickingSelectors';
import { getCurrentProductionComponentInstance } from '../selectors/scheduleStationSelectors';
import { getSharedComponentSetHasLayout } from '../selectors/sharedComponentSets';
import {
  createDoWorkingChangesExistOnComponentDesign,
  doWorkingChangesExistForActiveComponentDesign,
} from '../selectors/workingChangesSelectors';
import { setDimensionMode, setLastMemberLineSelectionPoint } from '../slices/applicationSlice';
import { getCurrentRenderMode } from '../slices/camera/selectors';
import { RenderMode } from '../slices/camera/types';
import { layoutSlice } from '../slices/layoutSlice';
import { analyzeSelectedPlates } from '../slices/livePlatingSlice';
import { addLocalUserDistanceDimension } from '../slices/localUserDimensionsSlice';
import {
  addComponentsToRenamePanel,
  expandNavigationPanelRows,
  removeComponentsFromRenamePanel,
  selectComponentInRenamePanel,
} from '../slices/panelDataSlice';
import {
  __UNSAFE_designItemsSelected,
  __UNSAFE_itemsDeselected,
  __UNSAFE_layoutItemsSelected,
  clearSelection,
  selectCheckResultsLoadCase,
  selectionWindowToolActivated,
  selectionWindowToolDeactivated,
  selectionWindowToolEnabled,
  selectSnaps,
} from '../slices/selectionsSlice';
import { updateCurrentProductionComponentInstanceGuid } from '../slices/stationsSlice';
import { clearHoverItem } from '../slices/viewportSlice';
import { setVisibleLoads } from '../slices/visualOptionsSlice';
import { onClickReferenceLineDuringBasicInsertion } from './basicInsertionActions';
import { setActiveCamera } from './cameraActions';
import { showDelayedNotifications } from './delayedNotificationActions';
import { finishSetBearingLine } from './editBearingActions';
import {
  finishSetMemberLine,
  finishSetReferenceLineForMemberCutGuid,
  finishSetReferenceLineGuid,
} from './insertActions';
import { loadAdditionalComponentDesignDataWithSpinner, MAX_CONCURRENT_COMPONENT_DESIGN_LOADS } from './loadActions';
import { cancelActiveAnalysisResultRequests } from './loadAnalysisResults';
import { handleReferenceLineClickDuringMemberCuttingMode } from './memberCuttingModeActions';
import { AsyncThunkActionVoid, ThunkActionReturn, ThunkActionVoid } from './thunkActions';
import { updateUrl, updateUrlFromState } from './urlActions';
import { showAnalogModel } from './visualOptionActions';

export const selectLoadCase =
  (guid: string | null): ThunkActionVoid =>
  (dispatch, getState) => {
    dispatch(selectCheckResultsLoadCase(guid));

    const state = getState();
    const selectedEntry = getSingleSelectedItemEntryOrNull(state);
    if (selectedEntry) {
      dispatch(
        updateUrl(
          state.selections.activeComponentDesignGuid,
          selectedEntry.type,
          'guid' in selectedEntry.item ? selectedEntry.item.guid : null,
          guid,
          state.analysisSets.activeAnalysisSetGuid ?? null,
        ),
      );
    }
  };

export interface ReferenceLineSelectionOptions {
  clickPosition?: Point2DInterface;
  intent?: 'select' | 'multi-select' | 'context-menu';
}

export const selectReferenceLine =
  (line: CalculatedLine, options?: ReferenceLineSelectionOptions): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const pickingObjectInfo = getPickingObjectInfo(state);
    const { inMemberCuttingMode } = state.application;

    if (inMemberCuttingMode && options?.clickPosition) {
      const selectedEntries = getSelectedItemEntries(state);
      if (selectedEntries.length === 1 && selectedEntries[0].type === 'member') {
        dispatch(
          handleReferenceLineClickDuringMemberCuttingMode(
            selectedEntries[0].item,
            line,
            Point2DClass.fromObject(options.clickPosition),
          ),
        );
        return;
      }
    }

    if (pickingObjectInfo && pickingObjectInfo.type === 'pickingReferenceLine') {
      switch (pickingObjectInfo.targetInfo.type) {
        case 'referenceLine':
          dispatch(
            finishSetReferenceLineGuid(
              pickingObjectInfo.targetInfo.rootGuid,
              pickingObjectInfo.targetInfo.key,
              line.guid,
              state.selections.activeComponentDesignGuid!,
            ),
          );
          return;

        case 'memberCut':
          dispatch(finishSetReferenceLineForMemberCutGuid(line.guid));
          return;

        case 'memberLine':
          if (options?.clickPosition) {
            dispatch(setLastMemberLineSelectionPoint({ ...options.clickPosition }));
          }

          dispatch(finishSetMemberLine(line.guid, options?.clickPosition));
          return;

        case 'bearingLine':
          dispatch(finishSetBearingLine(line.guid));
          return;
      }
    }

    const basicInsertionInfo = getBasicInsertionInformation(state);
    if (isBasicInsertionModeActive(basicInsertionInfo) && options?.clickPosition) {
      dispatch(onClickReferenceLineDuringBasicInsertion(line, options.clickPosition));
      return;
    }

    dispatch(selectItem(line, 'referenceLine', { exclusive: options?.intent !== 'multi-select' }));
  };

const displayHiddenPlateLabelsMessage = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  const activeComponent = getActiveComponentDesign(state);
  if (!activeComponent) {
    return;
  }

  const hideReasons = [];
  const { platePairs } = activeComponent.component;

  if (state.visualOptions.plateDetailSettings.hideForMostCommonType) {
    const mostCommonPlateType = getMostCommonPlateType(platePairs);
    if (mostCommonPlateType) {
      hideReasons.push(
        `type ${getPlateTypeString(mostCommonPlateType.plateType, mostCommonPlateType.tangiblePlateTypesForComboPlateType)}`,
      );
    }
  }

  if (state.visualOptions.plateDetailSettings.hideForMostCommonSize) {
    const mostCommonPlateSize = getMostCommonPlateSize(platePairs);
    if (mostCommonPlateSize) {
      hideReasons.push(`size ${getPlateSizeString(mostCommonPlateSize)}`);
    }
  }

  if (hideReasons.length > 0) {
    dispatch(enqueueInfo(`Labels are hidden for plate ${hideReasons.join(' and ')}`));
  }
};

const displayMultiPlyConnectionDetails = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const isAssemblyUser = hasAssemblyPermission(state);
  const activeComponentDesign = getActiveComponentDesign(state)!;
  const isInserting = isInsertingComponentDesign(state);
  const hasWorkingChanges = doWorkingChangesExistForActiveComponentDesign(state);
  const { component } = activeComponentDesign;
  if (!isAssemblyUser || isInserting || hasWorkingChanges || !isTruss(component) || component.numberOfPlies === 1) {
    return;
  }

  try {
    const multiPlyConnectionDetails = await designService.componentDesigns.getMultiPlyConnectionDetailNote(
      activeComponentDesign.guid,
    );
    dispatch(enqueueInfo(multiPlyConnectionDetails, true));
  } catch (error) {
    console.error(error);
    dispatch(enqueueWarning('Multi-ply truss. Verify connection details.'));
  }
};

export interface SelectionOptions {
  /**
   * `selectItem` / `selectItems` / `selectEntry` / `selectEntries` can be used to select, deselect, or toggle the
   *   selection status of the provided item(s) / entry(ies). Use this option to communicate your intent.
   *
   * Defaults to 'select' as that is the most common programmatic use case.
   */
  intent?: 'select' | 'deselect' | 'toggle';

  /**
   * This option allows you to ignore prohibitions against selecting certain types of items. Use with caution.
   */
  force?: boolean;

  /**
   * Whether other items may (depending on multi-select logic) remain selected in addition to the provided items.
   *
   * Defaults to `true` (selection is exclusive -- previous selections are cleared).
   */
  exclusive?: boolean;

  /**
   * If set, and the provided items cause a change in the active component design, any visible notifications will be
   *   preserved. Defaults to `false` since notifications are per-component by design.
   */
  retainNotifications?: boolean;

  /**
   * If set, ensures that the camera position will not be affected by this action. Defaults to `false` since this action
   *   normally will only change the camera position if required (i.e., when changing to layout mode).
   */
  retainCameraPosition?: boolean;

  /**
   * If set, an attempt will be made to respect the current layout hierarchy filter and exclusively show more levels
   *   if needed. This is a soft request and will not be respected in cases where, for example, the active building is
   *   being changed. Defaults to `true` for all objects except levels.
   */
  retainLayoutHierarchyFilters?: boolean;
}

const DEFAULT_SELECTION_OPTIONS = {
  intent: 'select',
  force: false,
  exclusive: true,
  retainNotifications: false,
  retainCameraPosition: false,
} satisfies Required<Omit<SelectionOptions, 'retainLayoutHierarchyFilters'>>;

export function selectItem(
  itemObject: ComponentDesign | ComponentDesignLayoutModel | null,
  type: 'componentDesign' | null,
  options?: SelectionOptions,
): ThunkActionVoid;

export function selectItem<T extends SelectableItemType>(
  itemObject: SelectableItemOfType<T> | null,
  type: T | null,
  options?: SelectionOptions,
): ThunkActionVoid;

export function selectItem<T extends SelectableItemType>(
  itemObject: SelectableItemOfType<T> | ComponentDesignLayoutModel | null,
  type: T | null,
  options?: SelectionOptions,
): ThunkActionVoid {
  return selectEntry({ item: itemObject, type } as SelectableItemEntry, options);
}

export function selectItems(
  itemObjects: (ComponentDesign | ComponentDesignLayoutModel)[],
  type: 'componentDesign' | null,
  options?: SelectionOptions,
): ThunkActionVoid;

export function selectItems<T extends SelectableItemType>(
  itemObjects: SelectableItemOfType<T>[],
  type: T | null,
  options?: SelectionOptions,
): ThunkActionVoid;

export function selectItems<T extends SelectableItemType>(
  itemObjects: SelectableItemOfType<T>[] | ComponentDesignLayoutModel[],
  type: T | null,
  options?: SelectionOptions,
): ThunkActionVoid {
  const entries =
    type == null
      ? []
      : (itemObjects
          .map((item) => createSelectedItemEntry(type, item as SelectableItemOfType<T>))
          .filter(notNullish) as SelectableItemEntryLikeArray);

  return selectEntries(entries, options);
}

export const forceClearSelection = (): ThunkActionVoid => (dispatch) => {
  dispatch(clearSelection());
  dispatch(clearHoverItem());
  dispatch(updateUrl(null, null, null, null, null));
};

type SelectableItemEntryLike =
  SelectableItemEntry | { type: 'componentDesign'; item: ComponentDesign | ComponentDesignLayoutModel };

type SelectableItemEntryLikeArray =
  SelectableItemEntry[] | { type: 'componentDesign'; item: ComponentDesign | ComponentDesignLayoutModel }[];

export function selectEntry(entry: SelectableItemEntryLike, options: SelectionOptions = {}): ThunkActionVoid {
  return selectEntries([entry] as SelectableItemEntryLikeArray, options);
}

export function selectEntries(
  entries: SelectableItemEntryLikeArray,
  providedOptions: SelectionOptions = {},
): ThunkActionVoid {
  return async (dispatch, getState) => {
    const type = entries[0].type;
    for (const entry of entries) {
      if (entry.type !== type) {
        assertUnlessInProduction('Attempt to select entries of different types which is not allowed.');
        return; // for production; don't get into an invalid state but don't crash
      }
    }

    const options: Required<SelectionOptions> = {
      ...DEFAULT_SELECTION_OPTIONS,

      // per the definition of this option, it should "Default to `true` for all objects except levels."
      retainLayoutHierarchyFilters: type !== 'level',

      ...providedOptions,
    };

    // set this if you need to ensure that the render mode is changed after selection
    let ensureRenderModeIs: RenderMode | undefined;

    const isLayoutItem = isConsideredLayoutItem(type);
    const isComponentDesignItem = isConsideredDesignItem(type);

    if (isLayoutItem === isComponentDesignItem) {
      assertUnlessInProduction(
        'Sanity check failed -- `selectEntries` assumes that layout and component design items are mutually exclusive.',
      );
    }

    if (isLayoutItem) {
      ensureRenderModeIs = 'layout';
    } else if (isComponentDesignItem) {
      ensureRenderModeIs = 'design';
    }

    // FIXME: imo it would be cleanest if all picking actions were handled here - David

    const canItemsBeSelected = options.force || getCurrentlySelectableItemTypes(getState()).has(type);
    if (!canItemsBeSelected) return;

    // `selectEntries([])` means "I don't want anything to be selected," which isn't possible, but we can fake it
    if (entries.length === 0 || entries.some((entry) => entry == null || entry.item == null)) {
      dispatch(resetSelectedItems());
      return;
    }

    const { insertingObjectInfo } = getState().application;
    const insertingMemberOrReferenceLine =
      insertingObjectInfo && (insertingObjectInfo.type === 'member' || insertingObjectInfo.type === 'referenceLine');

    if (insertingMemberOrReferenceLine && !options.force) {
      assertUnlessInProduction('selectEntry called while inserting a member or reference line, which is forbidden');
      return;
    }

    const currentlySelectedItems = getSelectedItemIdentifiers(getState());
    const allItemsAreCurrentlySelected = entries.every((entry) =>
      currentlySelectedItems.some((item) => item.type === entry.type && item.guid === entry.item.guid),
    );
    const otherItemsAreCurrentlySelected = getSelectedItemIdentifiers(getState()).some(
      (item) => item.type !== type || entries.some((entry) => entry.item.guid !== item.guid),
    );

    const selecting =
      options.intent === 'select' ||
      (options.intent === 'toggle' &&
        (!allItemsAreCurrentlySelected || (options.exclusive && otherItemsAreCurrentlySelected)));

    const deselecting = !selecting;

    // special cases during the bulk renaming panel -- selection is hijacked to control the set of components to rename
    const { renameLayoutComponentsPanel } = getState().panelData;
    if (type === 'trussEnvelope' && renameLayoutComponentsPanel.open) {
      const componentDesignGuids = entries
        .map((entry) => (entry.item as TrussEnvelope).componentDesignGuid)
        .filter(notNullish);

      if (componentDesignGuids.length > 0) {
        if (selecting) {
          if (
            componentDesignGuids.length === 1 &&
            renameLayoutComponentsPanel.componentDesignGuids.includes(componentDesignGuids[0])
          ) {
            dispatch(selectComponentInRenamePanel(componentDesignGuids[0]));
          } else {
            dispatch(addComponentsToRenamePanel({ componentDesignGuids }));
          }
        } else {
          dispatch(removeComponentsFromRenamePanel({ componentDesignGuids }));
        }

        return;
      }
    }

    // special cases for visibility systems not based on the selections slice
    switch (type) {
      case 'analogJoint':
      case 'analogLink':
        if (selecting) dispatch(showAnalogModel());
        break;
      case 'platePair':
        if (selecting && entries.length === 1) dispatch(analyzeSelectedPlates(entries.map((entry) => entry.item.guid)));
        break;

      case 'load':
      case 'loadCase':
        const involvedLoads = entries
          .map((entry) =>
            (() => {
              switch (entry.type) {
                case 'load':
                  return [entry.item];

                case 'loadCase':
                  return entry.item.loads;

                default:
                  throw new Error('unreachable');
              }
            })(),
          )
          .flat();

        // FIXME: this implementation will not work for multi-select; fine atm because loads can't be multi-selected

        if (selecting) {
          dispatch(setVisibleLoads(involvedLoads.map((load) => load.guid)));
        } else if (involvedLoads.some((load) => load.guid in getState().visualOptions.visibleLoadGuids)) {
          dispatch(setVisibleLoads([]));
        }

        break;
    }

    // get de-selection out of the way and return early so we don't have to worry about it for the rest of this action
    if (deselecting) {
      dispatch(clearHoverItem());

      const deselectingEverything = currentlySelectedItems.every(
        (item) => item.type === type && entries.some((entry) => item.guid === entry.item.guid),
      );

      if (deselectingEverything) {
        dispatch(resetSelectedItems());
      } else {
        // Safe because we have performed the necessary checks. This is one place permitted to dispatch this action.
        dispatch(__UNSAFE_itemsDeselected({ type, itemGuids: entries.map((entry) => entry.item.guid) }));
      }

      return;
    }

    // now we only have to worry about selection, and can start determining how that will be handled
    const canItemBeMultiSelected = hasMultiSelectPermission(getState()) || ITEMS_ALWAYS_MULTI_SELECTABLE.includes(type);
    const replacePreviousSelection =
      options.exclusive ||
      !canItemBeMultiSelected ||
      getSelectedItemIdentifiers(getState()).some((item) => item.type !== type);

    // Any component designs that are just placeholders need to be loaded before selecting them.
    //   Note: this is the only time this action is actually async, which is why most usages don't `await` it.
    if (type === 'componentDesign') {
      ensureRenderModeIs = 'design';

      const loaders = [];

      for (let i = 0; i < entries.length; i++) {
        const componentDesign = entries[i].item as ComponentDesign | ComponentDesignLayoutModel;

        if (!getState().componentDesigns.loadedState[componentDesign.guid].fullyLoaded) {
          loaders.push(async () => {
            await dispatch(loadAdditionalComponentDesignDataWithSpinner(componentDesign.guid));

            const loadedComponentDesign = getState().componentDesigns.data[componentDesign.guid];
            if (!loadedComponentDesign) throw new Error('Loaded component design not found.');

            entries[i] = { type: 'componentDesign', item: loadedComponentDesign };
          });
        }
      }

      if (loaders.length > 0) {
        await mapAsync(loaders, (loader) => loader(), { concurrency: MAX_CONCURRENT_COMPONENT_DESIGN_LOADS });
      }
    }

    // determine the active component design and whether it is changing

    let activeComponentDesignGuid: string | null;
    let { activeAnalysisSetGuid } = getState().analysisSets;

    if (type === 'componentDesign') {
      activeComponentDesignGuid = replacePreviousSelection
        ? entries[0].item.guid // make first selected component the active one
        : getState().selections.activeComponentDesignGuid; // preserve active component; we're just adding to selection
    } else if (type === 'productionComponentInstance') {
      activeComponentDesignGuid =
        getState().productionModels.analysisSetGuidToComponentDesignGuid[entries[0].item.analysisSetGuid];
    } else if (isConsideredDesignItem(entries[0].type) || !isLayoutModeEnabled(getState())) {
      activeComponentDesignGuid = getState().selections.activeComponentDesignGuid;
    } else {
      activeComponentDesignGuid = null;
    }

    const isChangingComponents =
      activeComponentDesignGuid && activeComponentDesignGuid !== getState().selections.activeComponentDesignGuid;
    const isSwitchingFromLayoutToDesign = isLayoutViewActive(getState()) && isComponentDesignItem;

    // additional steps are required when changing the active component design
    if (activeComponentDesignGuid && (isChangingComponents || isSwitchingFromLayoutToDesign)) {
      dispatch(setDimensionMode(false));
      dispatch(clearHoverItem());

      const newComponentDesign = getState().componentDesigns.data[activeComponentDesignGuid];
      if (newComponentDesign === undefined) throw new Error('Could not find new active component');

      // need to switch active analysis set
      const componentHasWorkingChanges = createDoWorkingChangesExistOnComponentDesign(newComponentDesign.guid)(
        getState(),
      );
      if (componentHasWorkingChanges) {
        activeAnalysisSetGuid = undefined;
      } else {
        const componentDesignHistory = getState().analysisHistory[newComponentDesign.guid];
        if (!componentDesignHistory) {
          activeAnalysisSetGuid = undefined;
        } else {
          const mostRecentAnalysis = getMostRecentAnalysis(Object.values(componentDesignHistory));
          activeAnalysisSetGuid = mostRecentAnalysis?.analysisSetGuid;
          const analysisSet = getState().analysisSets.sets.find(
            (possibleAnalysisSet) => possibleAnalysisSet.guid === activeAnalysisSetGuid,
          );
          if (!analysisSet) {
            activeAnalysisSetGuid = undefined;
          }
        }
      }

      cancelActiveAnalysisResultRequests();

      // manage notifications as needed
      if (!options.retainNotifications) dispatch(closeAllSnackbars());
      dispatch(showDelayedNotifications(activeComponentDesignGuid));
    }

    if (!replacePreviousSelection) {
      // If multi-selecting, we need to prevent duplicate items in `selectionsSlice` by ensuring that only items not
      //   already selected will be considered from here on out.
      //
      // Note that our startup process depends on selecting an item that is already selected, so ensure you don't break
      //   that if making any changes to the placement or implementation of this check.
      entries = entries.filter(
        (entry) => !currentlySelectedItems.some((item) => item.type === type && item.guid === entry.item.guid),
      ) as SelectableItemEntryLikeArray;
    }

    const componentDesignCount = Object.values(getState().componentDesigns.data).length;

    if ((isLayoutModeEnabled(getState()) || componentDesignCount === 0) && !isConsideredDesignItem(type)) {
      // Selecting a project or other container without an active component design is only allowed in layout mode
      //   in order to preserve the user-visible behavior without the feature flag. The exception is when there are no
      //   component designs in a project yet. Selecting the project, in order to change some settings for example,
      //   might be needed before the user creates any component designs.

      const getLevelGuid = createGetLevelGuid(getState());

      let buildingGuids;
      const levelGuids = new Set<string>();
      let selectionIncludesGhostObjects = false;

      const insertions = getInsertionsWithPendingChanges(getState());

      switch (type) {
        case 'trussGroup': {
          const trussGroupGuids = entries
            .filter((entry) => entry.type === 'trussGroup')
            .map((entry) => entry.item.guid);
          const trussGroups = trussGroupGuids.map((guid) => getState().trussGroups[guid]);
          const levelGuidsForTrussEnvelopes = uniq(
            trussGroups.flatMap((group) =>
              group.trussEnvelopeGuids
                .map((guid) => getState().layout.trussEnvelopes[guid]?.levelGuid)
                .filter(notNullish),
            ),
          );
          for (const levelGuid of levelGuidsForTrussEnvelopes) {
            levelGuids.add(levelGuid);
          }

          break;
        }

        case 'level': {
          for (const levelEntry of entries) {
            levelGuids.add(levelEntry.item.guid);
          }

          break;
        }

        case 'building': {
          buildingGuids = new Set(entries.map((entry) => entry.item.guid));

          const allLevelGuids = getAllLevelGuids(getState());

          for (const insertion of insertions) {
            if (buildingGuids.has(insertion.parentGuid) && allLevelGuids.has(insertion.objectGuid)) {
              levelGuids.add(insertion.objectGuid);
            }
          }

          break;
        }

        case 'project': {
          // If already in "ghost objects" mode we normally want to stay in it to avoid disorienting visual changes.
          //
          // The exceptions are a) if nothing is selected, b) if a project is selected, or c) if a component design
          //   object is selected -- these are all cases encountered during application startup or when switching to
          //   layout mode. In these cases, we either want to select a project or have already selected a project, but
          //   we also need to apply a hierarchy filter so that the project's primary building will be visible.
          if (
            getState().selections.layoutHierarchyFilter === 'ghost' &&
            getState().selections.selectedItems.length > 0 &&
            getState().selections.selectedItems[0].type !== 'project' &&
            getState().selections.selectedItems.every((item) => isConsideredLayoutItem(item.type))
          ) {
            selectionIncludesGhostObjects = true;
            break;
          }

          const allBuildingGuids = getAllBuildingGuids(getState());
          const chosenBuildingGuid = pickSingleActiveBuildingGuid(Array.from(allBuildingGuids));

          // If we can't find a building we're not in a hierarchy project.
          if (chosenBuildingGuid == null) {
            selectionIncludesGhostObjects = true;
            break;
          }

          buildingGuids = new Set([chosenBuildingGuid]);
          const allLevelGuids = getAllLevelGuids(getState());

          for (const insertion of insertions) {
            if (insertion.parentGuid === chosenBuildingGuid && allLevelGuids.has(insertion.objectGuid)) {
              levelGuids.add(insertion.objectGuid);
            }
          }

          break;
        }

        default: {
          for (const entry of entries) {
            if (entry.type === 'componentDesign') continue;
            if (!hasLevelGuid(entry)) continue;

            const levelGuid = getLevelGuid(entry.item);

            if (levelGuid === null) {
              selectionIncludesGhostObjects = true;
            } else {
              levelGuids.add(levelGuid);
            }
          }
        }
      }

      const allBuildingGuids = getAllBuildingGuids(getState());

      if (buildingGuids == null) {
        buildingGuids = new Set<string>();

        if (levelGuids.size > 0) {
          for (const insertion of insertions) {
            if (allBuildingGuids.has(insertion.parentGuid) && levelGuids.has(insertion.objectGuid)) {
              buildingGuids.add(insertion.parentGuid);
            }
          }
        }
      }

      if (levelGuids.size > 0 !== buildingGuids.size > 0) {
        throw new Error('Invalid layout hierarchy encountered: levels OR buildings exist but not both');
      }

      if (buildingGuids.size > 1) {
        throw new Error('Objects belonging to multiple buildings have been selected. This is prohibited.');
      }

      // multiple levels are allowed when selecting a building or project -- we want them all to be visible
      if (levelGuids.size > 1 && type !== 'building' && type !== 'project' && type !== 'trussGroup') {
        throw new Error('Objects belonging to multiple levels have been selected. This is prohibited.');
      }

      if (selectionIncludesGhostObjects && levelGuids.size > 0) {
        throw new Error('Ghost objects have been selected alongside proper hierarchy objects. This is prohibited.');
      }

      const hierarchyInfo = (() => {
        if (selectionIncludesGhostObjects) {
          return 'ghost';
        }

        // no hierarchy info if there's no hierarchy (shocking!)
        if (levelGuids.size === 0 && buildingGuids.size === 0) return null;

        return {
          containingLevelGuid: levelGuids.values().next().value!,
          containingBuildingGuid: buildingGuids.values().next().value!,

          relevantLevelGuids: Array.from(levelGuids),

          // retainLayoutHierarchyFilters is a soft request; not respected when bulk selection is occurring since we
          //   want to narrow visibility to the active level (among other cases enforced in the selections slice)
          attemptToPreservePreviousFilter: options.retainLayoutHierarchyFilters && options.exclusive,
        };
      })();

      dispatch(
        __UNSAFE_layoutItemsSelected({
          type,
          itemGuids: entries.map((entry) => entry.item.guid),
          replacePreviousSelection,

          hierarchyInfo,
        }),
      );
    } else {
      dispatch(
        __UNSAFE_designItemsSelected({
          type,
          itemGuids: entries.map((entry) => entry.item.guid),
          replacePreviousSelection,
          activeComponentDesignGuid,
          activeAnalysisSetGuid,
          changingComponentDesign: activeComponentDesignGuid !== getState().selections.activeComponentDesignGuid,
        }),
      );
    }

    if (activeComponentDesignGuid !== getState().selections.activeComponentDesignGuid) {
      dispatch(selectLoadCase(null));
    }

    let renderModeWasActuallyChanged = false;
    switch (ensureRenderModeIs) {
      case undefined:
        break;

      case 'layout':
        if (getCurrentRenderMode(getState()) === 'design' && isLayoutModeEnabled(getState())) {
          dispatch(setActiveCamera(getState().camera.activeLayoutCamera));
          renderModeWasActuallyChanged = true;
        }
        break;

      case 'design':
        if (getCurrentRenderMode(getState()) !== 'design') {
          dispatch(setActiveCamera(getState().camera.activeDesignCamera));
          renderModeWasActuallyChanged = true;
        }
        break;

      default:
        assertExhaustiveSwitch(ensureRenderModeIs);
    }

    const isChangingBuilding = type === 'building';
    const focusCameraDueToLayoutHierarchyChange = isChangingBuilding && !is3DLayoutCameraInUse(getState());

    if (
      !options.retainCameraPosition &&
      (isChangingComponents || focusCameraDueToLayoutHierarchyChange || renderModeWasActuallyChanged)
    ) {
      focusCameraOnSelectedObjects();
    }

    dispatch(updateUrlFromState());

    if (isChangingComponents || (type === 'componentDesign' && options.force)) {
      dispatch(displayMultiPlyConnectionDetails());
      dispatch(displayHiddenPlateLabelsMessage());
    }

    // If this is a schedule station, and we're changing the selected `ProductionComponentInstance`, update the server
    // with the new selection. If `selectEntry` was dispatched as the result of a synchronized loadStation action,
    // `station.currentProductionComponentInstanceGuid !== entry.item.guid` will properly return false and avoid
    // triggering a station update when it was only the passive receiver of an update.
    const station = getActiveStation(getState());
    if (
      type === 'productionComponentInstance' &&
      station &&
      station.followsSchedule &&
      station.currentProductionComponentInstanceGuid !== entries[0].item.guid
    ) {
      // Generally dimension mode is turned off when changing selections, but if the underlying component is the same,
      // this handles turning it off.
      dispatch(setDimensionMode(false));

      // FIXME: fine atm because production component instances can't be multi-selected; but refinement likely needed?
      const newCurrentProductionComponentInstanceGuid = entries[0].item.guid;

      // Sync-service should synchronize this change at the end of the call to `designService`, but in case it is down,
      // also dispatch this locally so that we can still advance through components. Do this before the server call so
      // we are not waiting on that.
      dispatch(
        updateCurrentProductionComponentInstanceGuid({
          stationGuid: station.guid,
          currentProductionComponentInstanceGuid: newCurrentProductionComponentInstanceGuid,
        }),
      );

      try {
        await designService.stations.setCurrentProductionComponentInstanceGuid(
          station.guid,
          newCurrentProductionComponentInstanceGuid,
        );
      } catch (error) {
        console.error(error);
        dispatch(enqueueError("Error updating station's current component on the server"));
      }
    }
  };
}

/**
 * Deselects the currently selected item(s) and selects an appropriate replacement item. Returns true if any items were
 *   deselected.
 */
export const resetSelectedItems = (): ThunkActionReturn<boolean> => (dispatch, getState) => {
  const state = getState();
  const selectedItems = getSelectedItemIdentifiers(state);

  const isOnlyItemSelected = (guid: string) => selectedItems.every((item) => item.guid === guid);

  // "deselecting" is non-intuitive because we generally need something to remain selected at all times. What should be
  //   selected to replace the currently selected item(s) depends on the context.
  const context = state.application.openContext;

  const options = {
    intent: 'select',
    force: true,
    retainCameraPosition: true,
    retainLayoutHierarchyFilters: true,
  } satisfies SelectionOptions;

  // If deselecting at a schedule station, select the grouped production component instance so that it is
  //   highlighted in the navigation panel.
  if (context?.type === 'schedule-station') {
    const station = getActiveStation(state);

    if (station !== null && station.currentProductionComponentInstanceGuid !== null) {
      const currentProductionComponentInstance = getCurrentProductionComponentInstance(state);
      dispatch(selectItem(currentProductionComponentInstance, 'productionComponentInstance', options));
      return true;
    }

    return false;
  }

  // In layout mode we don't want to reset to a non-layout object to avoid changing the user's camera.
  if (isLayoutViewActive(state)) {
    if (context?.type !== 'project' && context?.type !== 'shared-component-set') {
      assertUnlessInProduction(`Invalid context type for layout view: ${context?.type}`);
      return false;
    }

    // In a multi-level project we only want to reset to the active level to avoid changing the active building
    //   or resetting their level visibility filtering.
    const activeLevel = getActiveLayoutLevelGuidOrNull(state);
    if (activeLevel) {
      if (!isOnlyItemSelected(activeLevel)) {
        dispatch(selectItemByGuid(activeLevel, 'level', options));
        return true;
      } else {
        return false; // already on active level; don't fall back to the project
      }
    }

    const projectGuid = (() => {
      switch (context.type) {
        case 'project':
          return context.projectGuid;

        case 'shared-component-set':
          return getSharedComponentSetHasLayout(state)
            ? state.sharedComponentSet.sharedComponentSet?.sourceProjectGuid
            : undefined;

        default:
          assertExhaustiveSwitch(context);
      }
    })();

    if (projectGuid) {
      const project = state.layoutModels[projectGuid];
      if (project && !isOnlyItemSelected(project.guid)) {
        dispatch(selectItem(project, 'project', options));
        return true;
      }
    }

    return false;
  }

  // The default behavior (the only behavior before we complicated things with layouts and schedule stations) is to
  //   select the active component design.
  const activeComponentDesign = getActiveComponentDesign(state);
  if (activeComponentDesign && !isOnlyItemSelected(activeComponentDesign.guid)) {
    dispatch(selectItem(activeComponentDesign, 'componentDesign', options));
    return true;
  }

  return false;
};

export function selectItemByGuid(guid: string, type: SelectableItemType, options?: SelectionOptions): ThunkActionVoid {
  return async (dispatch, getState) => {
    const entryFactory = getSelectedEntryFactory(getState());
    const entry = entryFactory({ type, guid });

    if (entry == null) {
      throw new Error('Could not find entry to select.');
    }

    await dispatch(selectEntry(entry, options));
  };
}

export function selectItemsByGuid(
  guids: readonly string[],
  type: SelectableItemType,
  options?: SelectionOptions,
): ThunkActionVoid {
  return async (dispatch, getState) => {
    const entryFactory = getSelectedEntryFactory(getState());
    const entries = guids.map((guid) => entryFactory({ type, guid })).filter(notNullish);

    if (entries.length < guids.length) {
      throw new Error('Could not find some entries to select.');
    }

    await dispatch(selectEntries(entries, options));
  };
}

export function selectJackComponentEnvelopesOfComponentDesign(
  componentDesignGuid: string,
  options: SelectionOptions & { onlyIfVisible: boolean },
): ThunkActionVoid {
  return (dispatch, getState) => {
    const { onlyIfVisible, ...selectionOptions } = options;

    const state = getState();
    const trussEnvelopeGuidsByComponentDesignGuid = onlyIfVisible
      ? getVisibleTrussEnvelopeGuidsByComponentDesignGuid(state)
      : getTrussEnvelopeGuidsByComponentDesignGuid(state);

    const trussEnvelopeGuids = trussEnvelopeGuidsByComponentDesignGuid[componentDesignGuid];
    if (trussEnvelopeGuids == null || trussEnvelopeGuids.length === 0) return;

    dispatch(selectItemsByGuid(trussEnvelopeGuids, 'trussEnvelope', selectionOptions));
  };
}

export const moveToComponent =
  (component: ComponentDesign | ComponentDesignLayoutModel): ThunkActionVoid =>
  (dispatch, getState) => {
    if (getState().application.openContext?.type === 'schedule-station') {
      dispatch(enqueueError('Attempting to move to a component in this way is not supported in schedule stations'));
      return;
    }

    dispatch(selectItem(component, 'componentDesign'));
    dispatch(expandNavigationPanelRows(['componentDesignContainer', component.guid]));
  };

export const moveToPreviousComponent = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type === 'schedule-station') {
    const previousGroupedProductionComponentInstance = getPreviousGroupedProductionComponentInstance(state);
    if (previousGroupedProductionComponentInstance === null) return;
    const productionComponentInstanceByGuid = getProductionComponentInstanceByGuid(state);
    const firstProductionComponentInstanceInPreviousGroup =
      productionComponentInstanceByGuid[previousGroupedProductionComponentInstance.productionComponentInstanceGuids[0]];
    dispatch(selectItem(firstProductionComponentInstanceInPreviousGroup, 'productionComponentInstance'));
    return;
  }

  const previousComponent = getPreviousComponentDesign(state);
  if (!previousComponent) {
    return;
  }

  dispatch(moveToComponent(previousComponent));
};

export const moveToNextComponent = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type === 'schedule-station') {
    const nextGroupedProductionComponentInstance = getNextGroupedProductionComponentInstance(state);
    if (nextGroupedProductionComponentInstance === null) return;
    const productionComponentInstanceByGuid = getProductionComponentInstanceByGuid(state);
    const firstProductionComponentInstanceInNextGroup =
      productionComponentInstanceByGuid[nextGroupedProductionComponentInstance.productionComponentInstanceGuids[0]];
    dispatch(selectItem(firstProductionComponentInstanceInNextGroup, 'productionComponentInstance'));
    return;
  }

  const nextComponent = getNextComponentDesign(state);
  if (!nextComponent) {
    return;
  }

  dispatch(moveToComponent(nextComponent));
};

export const selectLoadCaseInUrlOrDefault = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  // This action only makes sense when a component design is active.
  const selectedItem = getSingleSelectedItemIdentifierOrNull(state);
  if (selectedItem == null || !isConsideredDesignItem(selectedItem.type)) return;

  const loadCases = getLoadCasesForActiveComponentDesign(state);

  const params = matchUrlPattern(location.pathname);

  // The `selectedLoadCaseGuid` is `0` by default when no load case is selected
  if (typeof params.selectedLoadCaseGuid === 'string' && params.selectedLoadCaseGuid !== '0') {
    if (loadCases.find(({ guid }) => guid === params.selectedLoadCaseGuid)) {
      dispatch(selectLoadCase(params.selectedLoadCaseGuid));
    } else {
      console.error('Cannot select Load Case in URL because none are loaded for the active component.');
      return;
    }
  } else {
    dispatch(selectLoadCase(null));
  }
};

export const setSelectedSnap =
  (snapPosition: Point2D | null): ThunkActionVoid =>
  (dispatch, getState) => {
    const { selectedSnaps } = getState().selections;
    if (!snapPosition) {
      dispatch(selectSnaps({ snap1: null, snap2: null }));
      return;
    }
    if (
      (selectedSnaps.snap1 !== null && selectedSnaps.snap2 !== null) ||
      (selectedSnaps.snap1 === null && selectedSnaps.snap2 === null)
    ) {
      dispatch(selectSnaps({ snap1: snapPosition, snap2: null }));
    } else if (selectedSnaps.snap1 !== null && !point2DEquals(selectedSnaps.snap1, snapPosition)) {
      if (isLayoutViewActive(getState())) {
        dispatch(
          layoutSlice.actions.userDistanceDimensionCreated({
            start: selectedSnaps.snap1,
            end: snapPosition,
            levelGuid: null,
          }),
        );
      } else {
        dispatch(addLocalUserDistanceDimension({ start: selectedSnaps.snap1, end: snapPosition }));
      }

      dispatch(selectSnaps({ snap1: null, snap2: null }));
    }
  };

export const selectActiveLoadCaseOrDefault = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  const oldLoadCaseGuid = state.selections.selectedCheckResultsLoadCaseGuid;
  const newLoadCaseGuids = getLoadCasesForActiveComponentDesign(state).map((loadCase) => loadCase.guid);
  if (oldLoadCaseGuid !== null && !newLoadCaseGuids.includes(oldLoadCaseGuid)) dispatch(selectLoadCase(null));
};

export const ensureSelectionWindowToolIsEnabled = (): ThunkActionVoid => (dispatch, getState) => {
  if (!getState().selections.isSelectionWindowToolEnabled) {
    dispatch(selectionWindowToolEnabled());
  }
};

export const ensureSelectionWindowToolIsActive = (): ThunkActionVoid => (dispatch, getState) => {
  if (!getState().selections.isSelectionWindowToolActive) {
    dispatch(selectionWindowToolActivated());
  }
};

export const ensureSelectionWindowToolIsDisabled = (): ThunkActionVoid => (dispatch, getState) => {
  const { selections } = getState();

  if (selections.isSelectionWindowToolEnabled || selections.isSelectionWindowToolActive) {
    dispatch(selectionWindowToolDeactivated());
  }
};
